type Entry = { revision: number; savedRevision: number; snapshot: unknown; timer: ReturnType<typeof setTimeout> | null; inFlight: Promise<void> | null };
type SaveResult = {pageId: number; message: string | null};
let reportSaveResult: (result: SaveResult) => void = () => {};
export function configurePageSaveNotifications(handler: (result: SaveResult) => void): void {
    reportSaveResult = handler;
}
const entries = new Map<string, Entry>();

function entryFor(pageId: number): Entry {
    const key = String(pageId);
    if (!entries.has(key)) entries.set(key, { revision: 0, savedRevision: 0, snapshot: null, timer: null, inFlight: null });
    return entries.get(key)!;
}

async function save(pageId: number, snapshot: unknown): Promise<void> {
    const { postData, prePublishPage } = await import("./post");
    const token = new URLSearchParams(window.location.search).get("token");
    if (token) {
        await postData(process.env.REACT_APP_BACKEND_ADDRESS + "/page/save/" + pageId, snapshot, false);
    } else {
        await prePublishPage(pageId, snapshot as object);
    }
}

export function schedulePageContentSave(pageId: number, snapshot: unknown, onError?: (error: unknown) => void): void {
    const entry = entryFor(pageId);
    entry.revision++;
    entry.snapshot = snapshot;
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
        entry.timer = null;
        void flushPageContent(pageId).catch((error) => onError?.(error));
    }, 500);
}

export async function flushPageContent(pageId: number, currentSnapshot?: unknown): Promise<number> {
    const entry = entryFor(pageId);
    if (currentSnapshot !== undefined && JSON.stringify(currentSnapshot) !== JSON.stringify(entry.snapshot)) {
        entry.revision++;
        entry.snapshot = currentSnapshot;
    } else if (entry.revision === 0) {
        const message = "No page content is available to save.";
        reportSaveResult({pageId, message});
        throw new Error(message);
    }
    if (entry.timer) {
        clearTimeout(entry.timer);
        entry.timer = null;
    }
    while (entry.savedRevision !== entry.revision) {
        if (entry.inFlight) {
            await entry.inFlight;
            continue;
        }
        const revision = entry.revision;
        const snapshot = entry.snapshot;
        entry.inFlight = save(pageId, snapshot);
        try {
            await entry.inFlight;
            entry.savedRevision = revision;
            if (entry.savedRevision === entry.revision) reportSaveResult({pageId, message: null});
        } catch (error) {
            reportSaveResult({pageId, message: error instanceof Error ? error.message : String(error)});
            throw error;
        } finally {
            entry.inFlight = null;
        }
    }
    return entry.revision;
}

export function isPageContentRevisionCurrent(pageId: number, revision: number): boolean {
    return entryFor(pageId).revision === revision;
}
